import { groupColors } from "@/configs/map/groups";
import { StadiaMap } from "@/features/map";
import { MapTooltip } from "@/features/workspace/content/map/tooltip/MapTooltip";
import { useActionHandler } from "@/hooks";
import { fileParserService, visualDataProcessor } from "@/services/data";
import {
  useDataProcessingStore,
  useOriginalData,
  useProcessedData,
} from "@/stores/data-processing";
import { useActiveDataflowId, useDataflowStore } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { useUIStore } from "@/stores/ui";
import { hasGeographicData } from "@/utils/data/hasGeographicData";
import { logger } from "@/utils/debug";
import { WebMercatorViewport } from "@deck.gl/core";
import { Alert, Button, Flex, Text } from "@mantine/core";
import type { Feature } from "geojson";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/*
DISCLAIMER: This is making me insane..
TODO: Refactor this to be more readable and maintainable.
*/

const EMPTY_ARRAY: string[] = [];

export function WorkspaceContentMap() {
  const activeDataflowId = useActiveDataflowId();
  const viewSelector = useCallback(
    (state: any) => {
      const workspaceUI = state.getWorkspaceUI(activeDataflowId);
      return workspaceUI?.view ?? "map";
    },
    [activeDataflowId]
  );
  const view = useUIStore(viewSelector);

  const originalData = useOriginalData(activeDataflowId || "");
  const processedData = useProcessedData(activeDataflowId || "");
  const { executeDataflow } = useDataProcessingStore();
  const addGeoJSONLayer = useMapStore((state) => state.addGeoJSONLayer);
  const removeGeoJSONLayer = useMapStore((state) => state.removeGeoJSONLayer);
  const loadGroupsConfig = useMapStore((state) => state.loadGroupsConfig);
  const [visualData, setVisualData] = useState<{
    data: any[];
    metadata: { columns: string[] };
  } | null>(null);
  const [tooltip, setTooltip] = useState<{
    feature: Feature | null;
    position: { x: number; y: number } | null;
    coordinate: [number, number] | null;
  }>({ feature: null, position: null, coordinate: null });

  const viewState = useMapStore(
    useCallback(
      (state) => {
        if (!activeDataflowId) return null;
        return state.mapInstances[activeDataflowId]?.viewState || null;
      },
      [activeDataflowId]
    )
  );

  const groupStateSelector = useCallback(
    (state: any) =>
      activeDataflowId ? state.groups[activeDataflowId] : undefined,
    [activeDataflowId]
  );
  const groupState = useMapStore(groupStateSelector);
  const groupColumnName = groupState?.groupColumnName ?? null;
  const visibleGroupsArray = groupState?.visibleGroups ?? EMPTY_ARRAY;
  const categoryConfigs = groupState?.groupConfigs ?? {};

  const updateGroupLayerFilter = useMapStore(
    (state) => state.updateGroupLayerFilter
  );
  const getGroupConfig = useMapStore((state) => state.getGroupConfig);
  const openModal = useUIStore((state) => state.openModal);

  const categoryColors = useMemo(() => {
    if (view !== "map" || !visualData?.data?.length) {
      return new Map<string, string>();
    }

    const hasLatLon = fileParserService.hasLatLonColumns(
      visualData.metadata.columns
    ).has;
    if (!hasLatLon || visualData.data.length >= 10000) {
      return new Map<string, string>();
    }

    if (!groupColumnName || visibleGroupsArray.length === 0) {
      return new Map<string, string>();
    }

    const hasCategoryColumn =
      visualData.metadata.columns.includes(groupColumnName);
    if (!hasCategoryColumn) {
      return new Map<string, string>();
    }

    // Use Set for faster lookups and iterate once
    const categorySet = new Set<string>();
    for (const row of visualData.data) {
      const value = row[groupColumnName];
      if (value != null && value !== "") {
        categorySet.add(String(value));
      }
    }

    // Build color map from sorted categories
    const colorMap = new Map<string, string>();
    const sortedCategories = Array.from(categorySet).sort();
    sortedCategories.forEach((category, index) => {
      const config = categoryConfigs[category];
      const defaultColor = groupColors[index % groupColors.length];
      colorMap.set(category, config?.color || defaultColor);
    });

    return colorMap;
  }, [
    view,
    visualData,
    groupColumnName,
    categoryConfigs,
    visibleGroupsArray.length,
  ]);

  const categoryColorsKey = useMemo(() => {
    if (!categoryColors || categoryColors.size === 0) return "";
    return Array.from(categoryColors.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([cat, color]) => `${cat}:${color}`)
      .join(",");
  }, [categoryColors]);

  const prevDataRef = useRef<{
    activeDataflowId: string | null;
    originalDataLength: number;
    processedDataLength: number;
    processedDataColumns: string;
  }>({
    activeDataflowId: null,
    originalDataLength: 0,
    processedDataLength: 0,
    processedDataColumns: "",
  });

  const activeDataflow = useDataflowStore((state) =>
    activeDataflowId ? state.dataflows[activeDataflowId] : undefined
  );

  const hasInputTransformer = activeDataflow?.flow_definition_json?.some(
    (t) => t.type === "user_file" || t.type === "data_input"
  );

  const hasDataflowLoaded = !!activeDataflow?.flow_definition_json;

  const loadAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeDataflowId && hasDataflowLoaded) {
      if (loadAttemptedRef.current !== activeDataflowId) {
        loadAttemptedRef.current = activeDataflowId;
        loadGroupsConfig(activeDataflowId).catch((error) => {
          logger.error(
            "[WorkspaceContentMap] Failed to load groups config:",
            error
          );
        });
      }
    }
    if (
      loadAttemptedRef.current &&
      loadAttemptedRef.current !== activeDataflowId
    ) {
      loadAttemptedRef.current = null;
    }
  }, [activeDataflowId, hasDataflowLoaded, loadGroupsConfig]);

  useEffect(() => {
    if (view !== "map") {
      setVisualData(null);
      return;
    }

    setVisualData(null);

    if (processedData && processedData.data && processedData.data.length > 0) {
      const columns = processedData.metadata?.columns || [];
      const latLonCheck = fileParserService.hasLatLonColumns(columns);

      if (!latLonCheck.has) {
        const dataLength = processedData.data.length;
        if (dataLength < 10000) {
          const hasGeometry = fileParserService.hasGeometryData(
            processedData.data
          );
          if (!hasGeometry) {
            setVisualData(null);
            return;
          }
        } else {
          setVisualData(null);
          return;
        }
      }
    }

    if (originalData && originalData.length > 0) {
      const columns =
        processedData?.metadata?.columns ||
        (originalData[0] ? Object.keys(originalData[0]) : []);
      const latLonCheck = fileParserService.hasLatLonColumns(columns);

      if (!latLonCheck.has && originalData.length < 10000) {
        const hasGeometry = fileParserService.hasGeometryData(originalData);
        if (!hasGeometry) {
          setVisualData(null);
          return;
        }
      } else if (!latLonCheck.has && originalData.length >= 10000) {
        setVisualData(null);
        return;
      }
    }

    if (!activeDataflowId) {
      setVisualData(null);
      prevDataRef.current = {
        activeDataflowId: null,
        originalDataLength: 0,
        processedDataLength: 0,
        processedDataColumns: "",
      };
      return;
    }

    if (
      (!originalData || originalData.length === 0) &&
      processedData &&
      processedData.data &&
      processedData.data.length > 0
    ) {
      setTimeout(() => {
        setVisualData({
          data: processedData.data,
          metadata: { columns: processedData.metadata.columns },
        });
      }, 0);
      return;
    }

    if (!originalData || originalData.length === 0) {
      setVisualData(null);
      prevDataRef.current = {
        activeDataflowId: null,
        originalDataLength: 0,
        processedDataLength: 0,
        processedDataColumns: "",
      };
      return;
    }

    const currentOriginalLength = originalData?.length || 0;
    const currentProcessedLength = processedData?.data?.length || 0;
    // Use faster string join instead of JSON.stringify
    const currentProcessedColumns = processedData?.metadata?.columns
      ? [...processedData.metadata.columns].sort().join(",")
      : "";
    const hasChanged =
      prevDataRef.current.activeDataflowId !== activeDataflowId ||
      prevDataRef.current.originalDataLength !== currentOriginalLength ||
      prevDataRef.current.processedDataLength !== currentProcessedLength ||
      prevDataRef.current.processedDataColumns !== currentProcessedColumns;

    if (!hasChanged) {
      if (visualData && visualData.data.length === currentProcessedLength) {
        return;
      }
      if (
        processedData &&
        processedData.data &&
        processedData.data.length > 0
      ) {
        setTimeout(() => {
          setVisualData({
            data: processedData.data,
            metadata: { columns: processedData.metadata.columns },
          });
        }, 0);
      }
      return;
    }

    prevDataRef.current = {
      activeDataflowId,
      originalDataLength: currentOriginalLength,
      processedDataLength: currentProcessedLength,
      processedDataColumns: currentProcessedColumns,
    };

    const timeoutId = setTimeout(() => {
      const processData = () => {
        visualDataProcessor
          .processDataForVisualization(
            originalData,
            undefined,
            processedData || undefined,
            activeDataflowId || undefined
          )
          .then((result) => {
            setVisualData({
              data: result.data,
              metadata: { columns: result.metadata.columns },
            });
          })
          .catch((error) => {
            logger.error(
              "[WorkspaceContentMap] processDataForVisualization failed:",
              error
            );
            setVisualData(null);
          });
      };

      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as any).requestIdleCallback(processData, { timeout: 1000 });
      } else {
        setTimeout(processData, 100);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [view, activeDataflowId, originalData, processedData]);

  useEffect(() => {
    if (!activeDataflowId || !hasInputTransformer) {
      return;
    }

    const { getProcessedData, loadProcessedDataFromStorage } =
      useDataProcessingStore.getState();
    const existingData = getProcessedData(activeDataflowId);

    if (!existingData && activeDataflowId) {
      loadProcessedDataFromStorage(activeDataflowId)
        .then((loadedData) => {
          if (!loadedData) {
            const dataflowData =
              useDataflowStore.getState().dataflows[activeDataflowId];
            if (dataflowData) {
              executeDataflow(activeDataflowId, dataflowData).catch((error) => {
                logger.error(
                  "[WorkspaceContentMap] executeDataflow failed:",
                  error
                );
              });
            }
          }
        })
        .catch((error) => {
          logger.error(
            "[WorkspaceContentMap] loadProcessedDataFromStorage failed:",
            error
          );
        });
    }
  }, [activeDataflowId, executeDataflow, hasInputTransformer]);

  useEffect(() => {
    if (view !== "map" || !activeDataflowId) {
      if (activeDataflowId) {
        removeGeoJSONLayer(`processed-${activeDataflowId}`);
      }
      return;
    }

    const layerId = `processed-${activeDataflowId}`;

    if (!visualData || !visualData.data || visualData.data.length === 0) {
      setTimeout(() => {
        removeGeoJSONLayer(layerId);
      }, 0);
      return;
    }

    const latLonCheck = fileParserService.hasLatLonColumns(
      visualData.metadata.columns
    );
    const hasLatLon = latLonCheck.has;

    if (!hasLatLon) {
      const dataLength = visualData.data.length;
      if (dataLength >= 10000) {
        setTimeout(() => {
          removeGeoJSONLayer(layerId);
        }, 0);
        return;
      }

      const currentVisualData = visualData;
      setTimeout(() => {
        if (!currentVisualData || !currentVisualData.data) {
          removeGeoJSONLayer(layerId);
          return;
        }
        const hasGeometry = fileParserService.hasGeometryData(
          currentVisualData.data
        );
        if (!hasGeometry) {
          removeGeoJSONLayer(layerId);
        }
      }, 0);
      return;
    }

    const data = visualData.data;
    const dataLength = data.length;
    const lat = latLonCheck.lat;
    const lon = latLonCheck.lon;

    const processLargeDataset = async () => {
      let finalHasGeometry: boolean | undefined;
      if (!hasLatLon && dataLength >= 10000) {
        finalHasGeometry = fileParserService.hasGeometryData(data);
      } else {
        finalHasGeometry = false;
      }

      if (finalHasGeometry) {
        const geoJSON = fileParserService.rowsToGeoJSON(data);
        addGeoJSONLayer(layerId, {
          fileId: layerId,
          fileName: "Processed Data",
          data: geoJSON,
          isVisible: true,
        });
      } else if (hasLatLon && lat && lon) {
        // Check if chunks are being used
        const chunks = groupState?.chunks;
        const useChunks = chunks && chunks.length > 0;
        const hasCategoryColumn =
          groupColumnName !== null &&
          visualData.metadata.columns.includes(groupColumnName);

        const CHUNK_SIZE = 1000;
        const features: GeoJSON.Feature[] = [];

        if (dataLength > CHUNK_SIZE) {
          for (let i = 0; i < dataLength; i += CHUNK_SIZE) {
            const chunk = data.slice(i, i + CHUNK_SIZE);

            const chunkFeatures = chunk
              .map((row) => {
                const latValue = row[lat];
                const lonValue = row[lon];

                if (
                  latValue === null ||
                  latValue === undefined ||
                  lonValue === null ||
                  lonValue === undefined
                ) {
                  return null;
                }

                const latNum =
                  typeof latValue === "number"
                    ? latValue
                    : parseFloat(String(latValue));
                const lonNum =
                  typeof lonValue === "number"
                    ? lonValue
                    : parseFloat(String(lonValue));

                if (
                  isNaN(latNum) ||
                  isNaN(lonNum) ||
                  latNum < -90 ||
                  latNum > 90 ||
                  lonNum < -180 ||
                  lonNum > 180
                ) {
                  return null;
                }

                const properties: any = { ...row };
                // Clear _category and _color when no group column is selected
                // This ensures all points use the same default color
                if (!groupColumnName || !hasCategoryColumn) {
                  delete properties._category;
                  delete properties._color;
                } else {
                  // Only set _category if a column is selected
                  // Don't set _color when using chunks - chunk colors are applied in createGeoJSONLayers
                  const categoryValue = row[groupColumnName];
                  // Normalize category value (trim whitespace, convert to string) for consistent matching
                  const category = categoryValue
                    ? String(categoryValue).trim()
                    : null;
                  properties._category = category;
                  // Only set _color when NOT using chunks (for individual group colors)
                  if (!useChunks && category) {
                    const color = categoryColors.get(category) || "#003399";
                    properties._color = color;
                  }
                }

                return {
                  type: "Feature" as const,
                  properties,
                  geometry: {
                    type: "Point" as const,
                    coordinates: [lonNum, latNum],
                  },
                };
              })
              .filter((feature) => feature !== null) as GeoJSON.Feature[];

            features.push(...chunkFeatures);

            if (i + CHUNK_SIZE < dataLength) {
              await new Promise((resolve) => setTimeout(resolve, 0));
            }
          }
        } else {
          const allFeatures = data
            .map((row) => {
              const latValue = row[lat];
              const lonValue = row[lon];

              if (
                latValue === null ||
                latValue === undefined ||
                lonValue === null ||
                lonValue === undefined
              ) {
                return null;
              }

              const latNum =
                typeof latValue === "number"
                  ? latValue
                  : parseFloat(String(latValue));
              const lonNum =
                typeof lonValue === "number"
                  ? lonValue
                  : parseFloat(String(lonValue));

              if (
                isNaN(latNum) ||
                isNaN(lonNum) ||
                latNum < -90 ||
                latNum > 90 ||
                lonNum < -180 ||
                lonNum > 180
              ) {
                return null;
              }

              const properties: any = { ...row };
              // Clear _category and _color when no group column is selected
              // This ensures all points use the same default color
              if (!groupColumnName || !hasCategoryColumn) {
                delete properties._category;
                delete properties._color;
              } else {
                // Only set _category if a column is selected
                // Don't set _color when using chunks - chunk colors are applied in createGeoJSONLayers
                const categoryValue = row[groupColumnName];
                // Normalize category value (trim whitespace, convert to string) for consistent matching
                const category = categoryValue
                  ? String(categoryValue).trim()
                  : null;
                properties._category = category;
                // Only set _color when NOT using chunks (for individual group colors)
                if (!useChunks && category) {
                  const color = categoryColors.get(category) || "#003399";
                  properties._color = color;
                }
              }

              return {
                type: "Feature" as const,
                properties,
                geometry: {
                  type: "Point" as const,
                  coordinates: [lonNum, latNum],
                },
              };
            })
            .filter((feature) => feature !== null) as GeoJSON.Feature[];

          features.push(...allFeatures);
        }

        if (features.length > 0) {
          const geoJSON: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features,
          };

          addGeoJSONLayer(layerId, {
            fileId: layerId,
            fileName: "Processed Data",
            data: geoJSON,
            isVisible: true,
          });
        } else {
          removeGeoJSONLayer(layerId);
        }
      } else {
        removeGeoJSONLayer(layerId);
      }
    };

    const timeoutId = setTimeout(() => {
      processLargeDataset().catch((error) => {
        logger.error(
          "[WorkspaceContentMap] processLargeDataset failed:",
          error
        );
        removeGeoJSONLayer(layerId);
      });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    view,
    visualData,
    activeDataflowId,
    addGeoJSONLayer,
    removeGeoJSONLayer,
    categoryColorsKey,
    groupColumnName,
    visibleGroupsArray.length,
    groupState?.chunks, // Track chunks changes to update when chunks are computed
  ]);

  useEffect(() => {
    if (!activeDataflowId || !visualData) {
      return;
    }
    updateGroupLayerFilter(
      activeDataflowId,
      `processed-${activeDataflowId}`,
      visualData.metadata.columns
    );
  }, [
    activeDataflowId,
    visualData?.metadata?.columns?.join(","),
    visibleGroupsArray.join(","),
    groupColumnName,
    updateGroupLayerFilter,
  ]);

  const handleHover = useCallback((_info: any) => {}, []);

  const handleEditGroup = useCallback(
    (groupValue: string, groupColor: string) => {
      if (!activeDataflowId) return;

      const config = getGroupConfig(activeDataflowId, groupValue);

      openModal(activeDataflowId, "group-config", "Configure Group", {
        groupValue,
        initialName: config?.name,
        initialColor: config?.color || groupColor,
        initialShape: config?.shape || "circle",
      });
    },
    [activeDataflowId, openModal, getGroupConfig]
  );

  const handleClick = useCallback(
    (info: any) => {
      if (!activeDataflowId || view !== "map") {
        setTooltip({ feature: null, position: null, coordinate: null });
        return;
      }

      if (
        info.object &&
        info.x !== undefined &&
        info.y !== undefined &&
        info.coordinate
      ) {
        const feature: Feature = {
          type: "Feature",
          properties: info.object.properties || info.object,
          geometry: info.object.geometry || {
            type: "Point",
            coordinates: info.coordinate || [0, 0],
          },
        };
        setTooltip({
          feature,
          position: { x: info.x, y: info.y },
          coordinate: info.coordinate as [number, number],
        });
      } else {
        setTooltip({ feature: null, position: null, coordinate: null });
      }
    },
    [activeDataflowId, view]
  );

  useEffect(() => {
    if (view !== "map") {
      setTooltip({ feature: null, position: null, coordinate: null });
    }
  }, [view]);

  const updateTooltipPosition = useCallback(
    (currentViewState: typeof viewState) => {
      if (!tooltip.coordinate || !currentViewState) {
        return;
      }

      try {
        const mapContainer = document.getElementById("workspace-map");
        if (mapContainer && currentViewState && tooltip.coordinate) {
          const width = mapContainer.clientWidth || 800;
          const height = mapContainer.clientHeight || 600;

          const viewport = new WebMercatorViewport({
            width,
            height,
            longitude: currentViewState.longitude,
            latitude: currentViewState.latitude,
            zoom: currentViewState.zoom,
            pitch: currentViewState.pitch || 0,
            bearing: currentViewState.bearing || 0,
          });

          const projected = viewport.project(tooltip.coordinate);
          if (
            projected &&
            projected[0] !== undefined &&
            projected[1] !== undefined
          ) {
            setTooltip((prev) => ({
              ...prev,
              position: { x: projected[0], y: projected[1] },
            }));
          }
        }
      } catch (error) {
        logger.warn("[MapTooltip] Failed to project coordinate:", error);
      }
    },
    [tooltip.coordinate]
  );

  useEffect(() => {
    updateTooltipPosition(viewState);
  }, [viewState, updateTooltipPosition]);

  const handleViewStateChange = useCallback(
    (newViewState: typeof viewState) => {
      if (newViewState) {
        updateTooltipPosition(newViewState);
      }
    },
    [updateTooltipPosition]
  );

  const [shouldRenderMap, setShouldRenderMap] = useState(false);

  useEffect(() => {
    if (view === "map") {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setShouldRenderMap(true);
        }, 0);
      });
    } else {
      setShouldRenderMap(false);
    }
  }, [view]);

  const hasGeographicDataCheck = processedData?.metadata?.columns
    ? hasGeographicData(processedData.data, processedData.metadata.columns)
        .hasGeographicData
    : false;

  const showAlert =
    view === "map" &&
    processedData &&
    processedData.data &&
    processedData.data.length > 0 &&
    !hasGeographicDataCheck;

  const actionHandler = useActionHandler();

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      pos="relative"
      w="100%"
      h="100%"
    >
      {showAlert && (
        <Alert
          color="yellow"
          bg="rgba(253, 235, 210, 0.8)"
          p={8}
          pos="absolute"
          bottom={15}
          w="100%"
          maw={350}
          style={{
            zIndex: 10,
            backdropFilter: "blur(10px)",
            border: "1px solid var(--mantine-color-yellow-6)",
          }}
        >
          <Flex justify="space-between" align="center" gap={8}>
            <Text px={4} c="yellow" fz={13} lh={1.4} fw={600}>
              No location data found.
            </Text>
            <Button
              color="yellow"
              size="xs"
              onClick={() => actionHandler("geocode")}
            >
              Geocode
            </Button>
          </Flex>
        </Alert>
      )}
      {shouldRenderMap ? (
        <StadiaMap
          width="100%"
          height="100%"
          interactive={true}
          workspaceId={activeDataflowId || undefined}
          id="workspace-map"
          onHover={handleHover}
          onClick={handleClick}
          onViewStateChange={handleViewStateChange}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "var(--mantine-color-background-0)",
          }}
        />
      )}
      <MapTooltip
        feature={tooltip.feature}
        position={tooltip.position}
        categoryConfigs={categoryConfigs}
        categoryColors={categoryColors}
        activeDataflowId={activeDataflowId}
        onEditGroup={handleEditGroup}
      />
    </Flex>
  );
}
